import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { ArrowDown2, ArrowUp2 } from "iconsax-react";
import { EmptyState } from "@/shared/components";
import { Badge, ButtonGroup, FileViewer } from "@/shared/ui";
import { formatDateForDisplay } from "@/shared/utils";
import type { SelfServiceProfile, WorkerProfile, VolunteerProfile } from "../../shared/types";
import type { ContractorProfile, ManagerProfile, ProfileDocument, SubDocument, DocumentCategory } from "../types";

interface Props {
  profile: SelfServiceProfile;
}

const WORKER_CATEGORIES: DocumentCategory[] = [
  "work_contract",
  "candidate_application",
  "leave",
  "amendment",
  "resignation",
];

const CONTRACTOR_CATEGORIES: DocumentCategory[] = ["service_agreement", "completion_act"];

const GROUPED_CATEGORIES: Set<DocumentCategory> = new Set([
  "candidate_application",
  "leave",
  "amendment",
  "resignation",
]);

const SUB_DOCUMENT_ORDER: Record<string, number> = {
  application: 0,
  order: 1,
  agreement: 2,
  personal_sheet: 3,
};

const WORK_CONTRACT_EN_ORDER: [string, number][] = [
  ["application", 0],
  ["order", 1],
];

function getWorkContractPriority(nameEn: string): number {
  const lower = nameEn.toLowerCase();
  for (const [keyword, priority] of WORK_CONTRACT_EN_ORDER) {
    if (lower.includes(keyword)) return priority;
  }
  return 2;
}

function getDocumentName(doc: { name: string; name_kk: string; name_en: string }, locale: string): string {
  if (locale === "kk") return doc.name_kk || doc.name;
  if (locale === "en") return doc.name_en || doc.name;
  return doc.name;
}

function getDocumentUrl(doc: { signed_pdf_url?: string | null; original_file_url?: string | null; download_url: string | null }): string | null {
  return doc.signed_pdf_url || doc.original_file_url || doc.download_url;
}

function getDocumentLabel(
  doc: { signed_pdf_url?: string | null; original_file_url?: string | null; download_url: string | null; is_legacy?: boolean },
  t: (key: string) => string,
): string | undefined {
  if (doc.is_legacy) return undefined;
  if (doc.signed_pdf_url) return t("overview.documents.signedVersion");
  if (doc.original_file_url) return t("overview.documents.originalVersion");
  return undefined;
}

export default function DocumentsSection({ profile }: Props) {
  const { t, i18n } = useTranslation("EmployeePortal");
  const locale = i18n.language;
  const categories = profile.user_type === "contractor" ? CONTRACTOR_CATEGORIES : WORKER_CATEGORIES;

  const [selectedCategory, setSelectedCategory] = useState<string>(categories[0]);
  const [expandedIds, setExpandedIds] = useState<Set<number | string>>(new Set());

  const documents: ProfileDocument[] = useMemo(() => {
    if (profile.user_type === "volunteer") return (profile as VolunteerProfile).documents ?? [];
    if (profile.user_type === "worker") return (profile as WorkerProfile).documents ?? [];
    if (profile.user_type === "manager") return (profile as ManagerProfile).documents ?? [];
    if (profile.user_type === "contractor") return (profile as ContractorProfile).documents ?? [];
    return [];
  }, [profile]);

  const filteredDocuments = useMemo(() => {
    const docs = documents.filter((doc) => doc.category === selectedCategory);
    if (selectedCategory === "work_contract") {
      docs.sort((a, b) => getWorkContractPriority(a.name_en) - getWorkContractPriority(b.name_en));
    }
    return docs;
  }, [documents, selectedCategory]);

  const categoryOptions = categories.map((cat) => ({
    label: t(`overview.documents.categories.${cat}`),
    value: cat,
  }));

  function handleToggleExpand(id: number | string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function renderSubDocument(subDoc: SubDocument, idx: number) {
    const subName = getDocumentName(subDoc, locale);
    const primaryUrl = getDocumentUrl(subDoc);

    if (subDoc.status === "signed" && subDoc.signed_pdf_url && subDoc.original_file_url) {
      return (
        <div key={idx} className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <FileViewer
              existingFileUrl={subDoc.signed_pdf_url}
              customFileName={subName}
              secondaryFileUrl={subDoc.original_file_url}
              secondaryFileName={subName}
              isDownloadable
            />
          </div>

        </div>
      );
    }

    return (
      <div key={idx} className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          {primaryUrl ? (
            <FileViewer
              existingFileUrl={primaryUrl}
              customFileName={subName}
              isDownloadable
            />
          ) : (
            <span className="text-body-regular-sm content-base-secondary truncate">{subName}</span>
          )}
        </div>
        {subDoc.is_legacy && <Badge variant="soft" color="gray" text={t("overview.documents.status.imported")} />}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <h2 className="text-display-2xs content-base-primary">{t("overview.documents.title")}</h2>

      <div className="flex">
        <ButtonGroup options={categoryOptions} value={selectedCategory} onChange={setSelectedCategory} />
      </div>

      {filteredDocuments.length === 0 ? (
        <EmptyState noDataText={t("overview.documents.noDocuments")} compact />
      ) : (
        <div className="flex flex-col gap-3">
          {filteredDocuments.map((doc, idx) => {
            const isGrouped = GROUPED_CATEGORIES.has(doc.category);
            const isExpanded = expandedIds.has(doc.id);
            const docName = getDocumentName(doc, locale);
            const docKey = `${doc.category}-${doc.id}-${idx}`;

            if (isGrouped) {
              return (
                <div key={docKey} className="flex flex-col border surface-base-stroke rounded-xl overflow-hidden">
                  <button
                    type="button"
                    onClick={() => handleToggleExpand(doc.id)}
                    className={cn(
                      "flex items-center justify-between gap-3 px-5 py-4 cursor-pointer hover:opacity-80 transition-opacity duration-150",
                    )}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-label-md content-base-primary truncate">{docName}</span>
                      {doc.date && (
                        <>
                          <span className="w-px h-4 border surface-base-stroke"></span>
                          <span className="text-body-regular-sm content-base-secondary shrink-0">
                            {formatDateForDisplay(doc.date)}
                          </span>
                        </>
                      )}
                    </div>
                    <span className="content-base-secondary shrink-0">
                      {isExpanded ? (
                        <ArrowUp2 size={20} color="currentColor" />
                      ) : (
                        <ArrowDown2 size={20} color="currentColor" />
                      )}
                    </span>
                  </button>

                  {isExpanded && doc.sub_documents && doc.sub_documents.length > 0 && (
                    <div className="flex flex-col gap-2 px-4 pb-3">
                      {[...doc.sub_documents]
                        .sort((a, b) => (SUB_DOCUMENT_ORDER[a.type] ?? 99) - (SUB_DOCUMENT_ORDER[b.type] ?? 99))
                        .map((subDoc, idx) => renderSubDocument(subDoc, idx))}
                    </div>
                  )}
                </div>
              );
            }

            const primaryUrl = getDocumentUrl(doc);
            const label = getDocumentLabel(doc, t);

            return (
              <div key={docKey} className="flex flex-col gap-2">
                {doc.signed_pdf_url && doc.original_file_url ? (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <FileViewer
                        existingFileUrl={doc.signed_pdf_url}
                        customFileName={docName}
                        secondaryFileUrl={doc.original_file_url}
                        secondaryFileName={docName}
                        isDownloadable
                        date={doc.date ? formatDateForDisplay(doc.date) : undefined}
                      />
                    </div>

                  </div>
                ) : primaryUrl ? (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <FileViewer
                        existingFileUrl={primaryUrl}
                        customFileName={label ? `${docName} (${label})` : docName}
                        isDownloadable
                        date={doc.date ? formatDateForDisplay(doc.date) : undefined}
                      />
                    </div>

                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className="text-body-regular-md content-base-secondary">{docName}</span>

                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
